import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface SyncEvent {
  sync_id: string;
  sync_type: string;
  started_at: string;
  completed_at: string | null;
  status: string;
  is_dry_run: boolean;
  total_nvdb_records: number;
  records_created: number;
  records_updated: number;
  records_unchanged: number;
  records_soft_deleted: number;
  records_skipped?: number;
  errors_count: number;
  duration_seconds: number | null;
  triggered_by: string | null;
}

interface SyncChange {
  change_id: string;
  entity_type: string;
  entity_id: string | null;
  entity_name?: string;
  entity_context?: string;
  nvdb_id: string | null;
  action: string;
  nvdb_data: any;
  local_data_before: any;
  local_data_after: any;
  fields_changed: string[] | null;
  error_message: string | null;
  created_at: string;
}

interface SyncEventDetails {
  audit: SyncEvent;
  changes: SyncChange[];
}


const SyncEvents: React.FC = () => {
  const { user } = useAuth();
  const [syncHistory, setSyncHistory] = useState<SyncEvent[]>([]);
  const [selectedSync, setSelectedSync] = useState<SyncEventDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showDetails, setShowDetails] = useState(false);
  const [showSkipDetails, setShowSkipDetails] = useState(false);
  const [filterAction, setFilterAction] = useState<string>('all');
  const [filterEntityType, setFilterEntityType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  
  // Scheduler state
  const [showSchedulerSettings, setShowSchedulerSettings] = useState(false);
  const [schedulerSettings, setSchedulerSettings] = useState({
    is_enabled: false,
    sync_times: ['08:00', '14:00', '20:00'],
    timezone: 'UTC',
    sync_type: 'full',
    is_dry_run: false
  });
  const [schedulerLoading, setSchedulerLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Check permissions
  const hasSyncAccess = user?.permissions?.includes('system.management') || user?.roleName === 'Admin';

  useEffect(() => {
    console.log('🔄 SyncEvents: Component mounted, hasSyncAccess:', hasSyncAccess);
    if (hasSyncAccess) {
      console.log('🔄 SyncEvents: Fetching sync data...');
      fetchSyncHistory();
      fetchSchedulerSettings();
    }
  }, [hasSyncAccess]);

  // Debug render state
  console.log('🔄 SyncEvents render state:', {
    hasSyncAccess,
    loading,
    error,
    syncHistoryLength: syncHistory.length,
    showDetails,
    showSchedulerSettings
  });

  const fetchSyncHistory = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.getNVDBSyncHistory(50); // Get more history
      if (response.success) {
        setSyncHistory(response.data.history);
      } else {
        setError('Failed to fetch sync history');
      }
    } catch (err) {
      console.error('Error fetching sync history:', err);
      setError('Failed to fetch sync history');
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncDetails = async (syncId: string) => {
    try {
      setDetailsLoading(true);
      setError('');
      
      const response = await apiService.getNVDBSyncDetails(syncId);
      if (response.success) {
        setSelectedSync(response.data);
        setShowDetails(true);
      } else {
        setError('Failed to fetch sync details');
      }
    } catch (err) {
      console.error('Error fetching sync details:', err);
      setError('Failed to fetch sync details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const triggerSync = async (isDryRun: boolean = false) => {
    try {
      setSyncLoading(true);
      setError('');
      
      // Note: V2 sync doesn't support dry-run mode yet, but we'll keep the parameter for future
      console.log(`🔄 Triggering NVDB Sync V2 (isDryRun: ${isDryRun})`);
      
      const response = await apiService.syncNVDBV2({});
      
      if (response.success) {
        // Refresh the sync history after a successful sync
        await fetchSyncHistory();
        setError('');
      } else {
        // Handle different error formats
        const errorMessage = response.message || 
                           'Unknown error occurred';
        setError(`Sync failed: ${errorMessage}`);
      }
    } catch (err: any) {
      console.error('Error triggering sync:', err);
      setError(`Failed to trigger sync: ${err.message || 'Network or server error'}`);
    } finally {
      setSyncLoading(false);
    }
  };

  const fetchSchedulerSettings = async () => {
    try {
      const response = await apiService.getNVDBSchedulerSettings();
      if (response.success) {
        setSchedulerSettings(response.data.settings);
      }
    } catch (err) {
      console.error('Error fetching scheduler settings:', err);
    }
  };

  const updateSchedulerSettings = async () => {
    try {
      setSchedulerLoading(true);
      setError('');
      
      const response = await apiService.updateNVDBSchedulerSettings(schedulerSettings);
      
      if (response.success) {
        setError('');
        setShowSchedulerSettings(false);
        // Refresh settings to get updated status
        await fetchSchedulerSettings();
      } else {
        setError(`Failed to update scheduler: ${response.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      console.error('Error updating scheduler:', err);
      setError(`Failed to update scheduler: ${err.message || 'Network or server error'}`);
    } finally {
      setSchedulerLoading(false);
    }
  };

  const addSyncTime = () => {
    const newTime = '12:00';
    setSchedulerSettings(prev => ({
      ...prev,
      sync_times: [...prev.sync_times, newTime]
    }));
  };

  const removeSyncTime = (index: number) => {
    setSchedulerSettings(prev => ({
      ...prev,
      sync_times: prev.sync_times.filter((_, i) => i !== index)
    }));
  };

  const updateSyncTime = (index: number, value: string) => {
    setSchedulerSettings(prev => ({
      ...prev,
      sync_times: prev.sync_times.map((time, i) => i === index ? value : time)
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds: number | string | null | undefined) => {
    // Convert to number and handle invalid values
    const numSeconds = typeof seconds === 'number' ? seconds : parseFloat(String(seconds || 0));
    
    // Handle invalid numbers
    if (isNaN(numSeconds) || numSeconds < 0) {
      return '0.0s';
    }
    
    if (numSeconds < 60) {
      return `${numSeconds.toFixed(1)}s`;
    }
    const minutes = Math.floor(numSeconds / 60);
    const remainingSeconds = numSeconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(1)}s`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/50';
      case 'failed':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/50';
      case 'running':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/50';
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-800';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/50';
      case 'updated':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/50';
      case 'soft_deleted':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/50';
      case 'unchanged':
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-800';
      case 'skipped':
        return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/50';
      case 'error':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/50';
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-800';
    }
  };

  // Filter changes for details view
  const getFilteredChanges = () => {
    if (!selectedSync || !selectedSync.changes) return [];
    
    let filtered = selectedSync.changes;
    
    if (filterAction !== 'all') {
      filtered = filtered.filter(change => change.action === filterAction);
    }
    
    if (filterEntityType !== 'all') {
      filtered = filtered.filter(change => change.entity_type === filterEntityType);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(change => 
        change.nvdb_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        change.entity_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        change.error_message?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  // Pagination for changes
  const getPaginatedChanges = () => {
    const filtered = getFilteredChanges();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = () => {
    return Math.ceil(getFilteredChanges().length / itemsPerPage);
  };

  // Debug early return
  if (false) { // Disabled - component is working correctly
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700">
        <h2>🔧 DEBUG: SyncEvents Component</h2>
        <p>hasSyncAccess: {String(hasSyncAccess)}</p>
        <p>loading: {String(loading)}</p>
        <p>error: {error || 'empty'}</p>
        <p>syncHistory length: {syncHistory.length}</p>
      </div>
    );
  }

  if (!hasSyncAccess) {
    console.log('🔄 SyncEvents: Returning access denied');
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">You don't have permission to access sync events.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    console.log('🔄 SyncEvents: Returning loading spinner');
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Error boundary - if there's a critical error, show error page
  if (error && error.includes('rendering')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-2">Error Loading Sync Events</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            There was an error rendering the sync events component.
          </p>
          <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-xs text-left mb-4">
            <strong>Error:</strong> {error}
          </div>
          <div className="space-x-2">
            <button 
              onClick={() => window.location.reload()} 
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Refresh Page
            </button>
            <button 
              onClick={() => console.log('Debug info:', { syncHistory, selectedSync, loading })} 
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
            >
              Log Debug Info
            </button>
          </div>
        </div>
      </div>
    );
  }

  console.log('🔄 SyncEvents: Returning main component JSX');
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">NVDB Sync Events</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Monitor NVDB synchronization operations and view detailed change logs
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => triggerSync(true)}
            disabled={syncLoading}
            className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            {syncLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            )}
            Dry Run Test
          </button>
          <button
            onClick={() => triggerSync(false)}
            disabled={syncLoading}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            {syncLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Run Full Sync
          </button>
          <button
            onClick={() => setShowSchedulerSettings(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Auto Schedule
          </button>
          <button
            onClick={fetchSyncHistory}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {error && error.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Sync History Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Sync Operations History</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Records
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Changes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {syncHistory.map((sync) => (
                <tr key={sync.sync_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <div>{formatDate(sync.started_at)}</div>
                    {sync.is_dry_run && (
                      <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">DRY RUN</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <span className="capitalize">{sync.sync_type}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(sync.status)}`}>
                      {sync.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <div className="text-xs space-y-1">
                      <div>Total: {sync.total_nvdb_records}</div>
                      {sync.errors_count > 0 && (
                        <div className="text-red-600 dark:text-red-400">Errors: {sync.errors_count}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <div className="text-xs space-y-1">
                      <div className="flex gap-4">
                        <span className="text-green-600 dark:text-green-400">+{sync.records_created}</span>
                        <span className="text-blue-600 dark:text-blue-400">~{sync.records_updated}</span>
                        <span className="text-red-600 dark:text-red-400">-{sync.records_soft_deleted}</span>
                        <span className="text-orange-600 dark:text-orange-400">⚠{sync.records_skipped || 0}</span>
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">
                        ={sync.records_unchanged} unchanged
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {formatDuration(sync.duration_seconds || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => fetchSyncDetails(sync.sync_id)}
                      disabled={detailsLoading}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {syncHistory.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No sync events</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              No NVDB sync operations have been performed yet.
            </p>
          </div>
        )}
      </div>

      {/* Sync Details Modal */}
      {showDetails && selectedSync && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Sync Event Details
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedSync?.audit?.started_at && formatDate(selectedSync?.audit?.started_at || '')} - {selectedSync?.audit?.sync_type}
                  {selectedSync?.audit?.is_dry_run && (
                    <span className="ml-2 text-orange-600 dark:text-orange-400 font-medium">(DRY RUN)</span>
                  )}
                </p>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Skip Details Button */}
              {selectedSync?.changes?.filter(c => c.action === 'skipped').length > 0 && (
                <div className="mb-4">
                  <button
                    onClick={() => setShowSkipDetails(true)}
                    className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    View Skip Details ({selectedSync?.changes?.filter(c => c.action === 'skipped').length} records)
                  </button>
                </div>
              )}

              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Operation Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div>Status: <span className={`font-medium ${selectedSync?.audit?.status === 'completed' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{selectedSync?.audit?.status}</span></div>
                    <div>Duration: {formatDuration(selectedSync?.audit?.duration_seconds || 0)}</div>
                    <div>Total Records: {selectedSync?.audit?.total_nvdb_records || 0}</div>
                  </div>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Changes Made</h4>
                  <div className="space-y-1 text-sm">
                    <div className="text-green-600 dark:text-green-400">Created: {selectedSync?.audit?.records_created || 0}</div>
                    <div className="text-blue-600 dark:text-blue-400">Updated: {selectedSync?.audit?.records_updated || 0}</div>
                    <div className="text-red-600 dark:text-red-400">Deleted: {selectedSync?.audit?.records_soft_deleted || 0}</div>
                    <div className="text-gray-600 dark:text-gray-400">Unchanged: {selectedSync?.audit?.records_unchanged || 0}</div>
                  </div>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Quality</h4>
                  <div className="space-y-1 text-sm">
                    <div className={(selectedSync?.audit?.errors_count || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                      Errors: {selectedSync?.audit?.errors_count || 0}
                    </div>
                    <div className="text-orange-600 dark:text-orange-400">
                      Skipped: {selectedSync?.changes?.filter(c => c.action === 'skipped').length || 0}
                    </div>
                    <div>Total Changes: {selectedSync?.changes?.length || 0}</div>
                  </div>
                </div>
              </div>

              {/* Filters for Changes */}
              <div className="flex flex-wrap gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Action</label>
                  <select
                    value={filterAction}
                    onChange={(e) => setFilterAction(e.target.value)}
                    className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="all">All Actions</option>
                    <option value="created">Created</option>
                    <option value="updated">Updated</option>
                    <option value="soft_deleted">Deleted</option>
                    <option value="unchanged">Unchanged</option>
                    <option value="skipped">Skipped</option>
                    <option value="error">Errors</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Entity Type</label>
                  <select
                    value={filterEntityType}
                    onChange={(e) => setFilterEntityType(e.target.value)}
                    className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm"
                  >
                    <option value="all">All Types</option>
                    <option value="company">Companies</option>
                    <option value="customer">Customers</option>
                    <option value="device">Devices</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search NVDB ID, Entity ID..."
                    className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>
              </div>

              {/* Changes Table */}
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                    Entity Changes ({getFilteredChanges().length})
                  </h4>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Entity
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Action
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          NVDB ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Changes
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Error
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {getPaginatedChanges().map((change) => (
                        <tr key={change.change_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">

                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            <div className="capitalize font-medium">{change.entity_type}</div>
                            {change.entity_name ? (
                              <div className="text-sm text-gray-900 dark:text-white">
                                {change.entity_name}
                              </div>
                            ) : change.entity_id && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                {change.entity_id.substring(0, 8)}...
                              </div>
                            )}
                            {change.entity_context && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {change.entity_context}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(change.action)}`}>
                              {change.action}
                            </span>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white font-mono">
                            {change.nvdb_id || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            {change.fields_changed && change.fields_changed.length > 0 ? (
                              <div className="space-y-1">
                                <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                  {change.fields_changed.length} field{change.fields_changed.length > 1 ? 's' : ''} changed:
                                </div>
                                <div className="text-xs text-blue-600 dark:text-blue-400">
                                  {change.fields_changed.slice(0, 3).map(field => 
                                    field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                  ).join(', ')}
                                  {change.fields_changed.length > 3 && ` +${change.fields_changed.length - 3} more`}
                                </div>
                              </div>
                            ) : change.action === 'created' ? (
                              <span className="text-green-600 dark:text-green-400 text-xs">New record created</span>
                            ) : change.action === 'skipped' ? (
                              <div className="text-xs">
                                <span className="text-orange-600 dark:text-orange-400 font-medium">Skipped</span>
                                {change.error_message && (
                                  <div className="text-gray-600 dark:text-gray-400 mt-1 max-w-xs">
                                    {change.error_message.replace('SKIPPED: ', '')}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-500 dark:text-gray-400">-</span>
                            )}
                          </td>
                                                      <td className="px-4 py-3 text-sm text-red-600 dark:text-red-400">
                              {change.error_message ? (
                                <div className="text-xs max-w-xs truncate" title={change.error_message}>
                                  {change.error_message}
                                </div>
                              ) : (
                                <span className="text-gray-500 dark:text-gray-400">-</span>
                              )}
                            </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {getTotalPages() > 1 && (
                  <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, getFilteredChanges().length)} of {getFilteredChanges().length} changes
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === getTotalPages()}
                        className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}

                {getFilteredChanges().length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">No changes match the current filters.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowDetails(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scheduler Settings Modal */}
      {showSchedulerSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  📅 Automated NVDB Sync Scheduler
                </h3>
                <button
                  onClick={() => setShowSchedulerSettings(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Enable/Disable Scheduler */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-900 dark:text-white">
                    Enable Automated Sync
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Automatically run NVDB sync at scheduled times daily
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={schedulerSettings.is_enabled}
                    onChange={(e) => setSchedulerSettings(prev => ({ ...prev, is_enabled: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Sync Times */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Daily Sync Times (Up to 3 times per day)
                </label>
                <div className="space-y-2">
                  {schedulerSettings.sync_times.map((time, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={time}
                        onChange={(e) => updateSyncTime(index, e.target.value)}
                        className="block w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                      <button
                        onClick={() => removeSyncTime(index)}
                        className="text-red-600 hover:text-red-800 p-1"
                        disabled={schedulerSettings.sync_times.length <= 1}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  {schedulerSettings.sync_times.length < 3 && (
                    <button
                      onClick={addSyncTime}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Time
                    </button>
                  )}
                </div>
              </div>

              {/* Sync Type */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Sync Type
                </label>
                <select
                  value={schedulerSettings.sync_type}
                  onChange={(e) => setSchedulerSettings(prev => ({ ...prev, sync_type: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="full">Full Sync (Companies, Customers, Devices)</option>
                  <option value="companies">Companies Only</option>
                  <option value="customers">Customers Only</option>
                  <option value="devices">Devices Only</option>
                </select>
              </div>

              {/* Dry Run Mode */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-900 dark:text-white">
                    Dry Run Mode
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Preview changes without making actual database modifications
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={schedulerSettings.is_dry_run}
                    onChange={(e) => setSchedulerSettings(prev => ({ ...prev, is_dry_run: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowSchedulerSettings(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={updateSchedulerSettings}
                disabled={schedulerLoading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                {schedulerLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Skip Details Modal */}
      {showSkipDetails && selectedSync && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Skip Details - NVDB Data Quality Issues
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {selectedSync.changes?.filter(c => c.action === 'skipped').length} records were skipped during sync
                </p>
              </div>
              <button
                onClick={() => setShowSkipDetails(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Skip Reasons Summary */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Skip Reasons Breakdown</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(() => {
                    const skipReasons = selectedSync.changes
                      ?.filter(c => c.action === 'skipped')
                      .reduce((acc, change) => {
                        const reason = change.error_message?.replace('SKIPPED: ', '').split(' (')[0] || 'Unknown';
                        acc[reason] = (acc[reason] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>);
                    
                    return Object.entries(skipReasons || {}).map(([reason, count]) => (
                      <div key={reason} className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
                        <div className="text-sm font-medium text-orange-800 dark:text-orange-200">{reason}</div>
                        <div className="text-lg font-bold text-orange-600 dark:text-orange-400">{count} records</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Detailed Skip Records */}
              <div>
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Detailed Skip Records</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          NVDB ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Entity Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Skip Reason
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          NVDB Data Summary
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Action Required
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {selectedSync.changes
                        ?.filter(c => c.action === 'skipped')
                        .map((change, index) => {
                          const nvdbData = change.nvdb_data ? JSON.parse(change.nvdb_data) : {};
                          const skipReason = change.error_message?.replace('SKIPPED: ', '') || 'Unknown reason';
                          
                          return (
                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-white">
                                {change.nvdb_id || 'N/A'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                  {change.entity_type}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="text-orange-600 dark:text-orange-400 font-medium">
                                  {skipReason.split(' (')[0]}
                                </div>
                                {skipReason.includes('(') && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {skipReason.split(' (')[1]?.replace(')', '')}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="max-w-xs">
                                  {change.entity_type === 'customer' ? (
                                    <div className="space-y-1 text-xs">
                                      <div><span className="font-medium">Email:</span> {nvdbData.customerEmail || 'N/A'}</div>
                                      <div><span className="font-medium">Name:</span> {nvdbData.customerContact || 'N/A'}</div>
                                      <div><span className="font-medium">Company:</span> {nvdbData.companyName || 'N/A'}</div>
                                    </div>
                                  ) : change.entity_type === 'device' ? (
                                    <div className="space-y-1 text-xs">
                                      <div><span className="font-medium">Serial:</span> {nvdbData.serialNumber || 'N/A'}</div>
                                      <div><span className="font-medium">Model:</span> {nvdbData.deviceModel || 'N/A'}</div>
                                      <div><span className="font-medium">Company:</span> {nvdbData.companyName || 'N/A'}</div>
                                    </div>
                                  ) : (
                                    <div className="text-xs text-gray-500">Raw data available</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="text-xs">
                                  {skipReason.includes('Missing NVDB Customer ID') && (
                                    <div className="text-red-600 dark:text-red-400">
                                      <div className="font-medium">Fix in NVDB:</div>
                                      <div>Add customer ID to record</div>
                                    </div>
                                  )}
                                  {skipReason.includes('Missing Email') && (
                                    <div className="text-red-600 dark:text-red-400">
                                      <div className="font-medium">Fix in NVDB:</div>
                                      <div>Add valid email address</div>
                                    </div>
                                  )}
                                  {skipReason.includes('Invalid Email Format') && (
                                    <div className="text-red-600 dark:text-red-400">
                                      <div className="font-medium">Fix in NVDB:</div>
                                      <div>Correct email format</div>
                                      <div className="text-gray-500">Current: {nvdbData.customerEmail}</div>
                                    </div>
                                  )}
                                  {skipReason.includes('Missing First Name') && (
                                    <div className="text-red-600 dark:text-red-400">
                                      <div className="font-medium">Fix in NVDB:</div>
                                      <div>Add first name to contact</div>
                                    </div>
                                  )}
                                  {skipReason.includes('Missing Serial Number') && (
                                    <div className="text-red-600 dark:text-red-400">
                                      <div className="font-medium">Fix in NVDB:</div>
                                      <div>Add serial number to device</div>
                                    </div>
                                  )}
                                  {skipReason.includes('Data Unchanged') && (
                                    <div className="text-green-600 dark:text-green-400">
                                      <div className="font-medium">No action needed</div>
                                      <div>Record is up to date</div>
                                    </div>
                                  )}
                                  {skipReason.includes('Duplicate Prevention') && (
                                    <div className="text-yellow-600 dark:text-yellow-400">
                                      <div className="font-medium">Review in NVDB:</div>
                                      <div>Check for duplicate records</div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Use this information to fix data quality issues in your NVDB system
              </div>
              <button
                onClick={() => setShowSkipDetails(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncEvents;
